<script setup lang="ts">
import { ref } from 'vue';

import {
    type CapabilityAudience,
    setTenantCapability,
    type TenantCapability
} from '@/services/tenant_capabilities';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useTenantStore } from '@/stores/tenant';

const AUDIENCE_LABELS: Record<CapabilityAudience, string> = {
    nobody: 'No one',
    admins: 'Admins only',
    members: 'All users'
};

const HELD_BY_SOMEONE: CapabilityAudience[] = ['admins', 'members'];

const PERMISSIONS: {
    capability: TenantCapability;
    label: string;
    description: string;
    audiences: CapabilityAudience[];
}[] = [
    {
        capability: 'create_private_theme',
        label: 'Create private themes',
        description:
            'Create a Slide Studio theme, kept private to its author unless shared.',
        audiences: HELD_BY_SOMEONE
    },
    {
        capability: 'publish_public_theme',
        label: 'Create public themes',
        description:
            'Publish a Slide Studio theme to the whole workspace, or turn a private one public.',
        audiences: HELD_BY_SOMEONE
    },
    {
        capability: 'publish_public_document',
        label: 'Create public presentations',
        description:
            'Add a publicly shared presentation to the workspace library.',
        audiences: HELD_BY_SOMEONE
    },
    {
        capability: 'create_datasource',
        label: 'Create data sources',
        description: 'Create a new data source in the workspace.',
        audiences: HELD_BY_SOMEONE
    },
    {
        capability: 'publish_public_datasource',
        label: 'Create public data sources',
        description:
            'Share a data source with the whole workspace, or turn an existing one public.',
        audiences: HELD_BY_SOMEONE
    },
    {
        capability: 'publish_public_custom_agent',
        label: 'Create public agents',
        description:
            'Share a custom agent with the whole workspace, or turn an existing one public.',
        audiences: HELD_BY_SOMEONE
    },
    {
        capability: 'publish_public_project',
        label: 'Create public projects',
        description:
            'Share a project with the whole workspace, or turn an existing one public.',
        audiences: HELD_BY_SOMEONE
    },
    {
        capability: 'share_project',
        label: 'Share projects',
        description:
            'Share a project with chosen users, or turn an existing one shared.',
        audiences: HELD_BY_SOMEONE
    },
    {
        capability: 'view_quota',
        label: 'See model usage quota',
        description:
            'Show the share of the model usage quota already spent under the chat bar.',
        audiences: ['nobody', 'admins', 'members']
    }
];

const tenantStore = useTenantStore();
const accessContextStore = useAccessContextStore();

const savingCapabilities = ref(new Set<TenantCapability>());
const error = ref<string | null>(null);

const audienceOf = (capability: TenantCapability) =>
    accessContextStore.capabilityAudiences[capability];

async function setAudience(
    capability: TenantCapability,
    audience: CapabilityAudience
) {
    const tenantId = tenantStore.tenantId;
    if (!tenantId || audienceOf(capability) === audience) return;

    savingCapabilities.value.add(capability);
    error.value = null;
    try {
        accessContextStore.capabilityAudiences = await setTenantCapability(
            tenantId,
            capability,
            audience
        );
    } catch (err) {
        console.error(err);
        error.value = 'Failed to update permissions.';
    } finally {
        savingCapabilities.value.delete(capability);
    }
}
</script>

<template>
    <div class="flex flex-col gap-6">
        <div>
            <h2 class="text-xl font-semibold">Permissions</h2>
            <p class="text-sm text-muted-foreground mt-1">
                Choose who can do what in your workspace.
            </p>
        </div>

        <div
            v-if="error"
            class="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
        >
            <p class="text-sm text-destructive">{{ error }}</p>
        </div>

        <div class="border rounded-xl divide-y">
            <div
                v-for="permission in PERMISSIONS"
                :key="permission.capability"
                class="flex items-center justify-between gap-6 p-4"
            >
                <div class="min-w-0">
                    <p class="text-sm font-medium">{{ permission.label }}</p>
                    <p class="text-sm text-muted-foreground">
                        {{ permission.description }}
                    </p>
                </div>

                <div
                    class="inline-flex shrink-0 items-center rounded-md bg-muted p-1"
                >
                    <button
                        v-for="audience in permission.audiences"
                        :key="audience"
                        type="button"
                        class="rounded-sm px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50"
                        :class="
                            audienceOf(permission.capability) === audience
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        "
                        :disabled="
                            savingCapabilities.has(permission.capability)
                        "
                        @click="setAudience(permission.capability, audience)"
                    >
                        {{ AUDIENCE_LABELS[audience] }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
